import { getEntryTypeMeta, mapLegacyType } from "../../../constants/timeline";
import { getLogTypeByCategory, getLogTypeByEntry, isBehaviorIncidentEntry, LOG_TYPES } from "../../../constants/logTypeRegistry";
import { getCareLogCategoryMeta } from "../../UI/careLogCardTheme";
import {
  getTimelineEntryBehaviorType,
  getTimelineEntryEditTarget,
  isSmartCaptureObservationEntry,
} from "../../../utils/timelineEntryBehavior";
import { resolveAdaptiveResponsePresentation } from "../../Dashboard/shared/adaptiveQuickLogConfig";
import { resolveAdaptiveFoodLogPresentation } from "../../Dashboard/shared/adaptiveFoodLogConfig";
import { resolveAdaptiveBathroomLogPresentation } from "../../Dashboard/shared/adaptiveBathroomLogConfig";

const getBehaviorHeadline = (entry) => String(
  entry.title
  || entry.summary
  || entry.description
  || entry.incidentData?.description
  || entry.notes
  || entry.content
  || entry.triggerSummary
  || entry.incidentData?.triggerSummary
  || 'Behavior'
).trim();

const getMoodValue = (entry) => String(
  entry.moodValue
  || entry.value
  || entry.data?.level
  || entry.content
  || entry.text
  || entry.notes
  || 'Calm'
).trim();

const hasStructuredBehaviorIncidentData = (entry = {}) => Boolean(
  entry?.incidentStyle
  || (
    entry?.incidentData
    && typeof entry.incidentData === 'object'
    && (
      entry.incidentData.description
      || entry.incidentData.notes
      || entry.incidentData.triggerSummary
      || entry.incidentData.severity
      || entry.incidentData.severityLabel
      || entry.incidentData.remedy
      || entry.incidentData.contextSnapshot
      || entry.incidentData.customIncidentName
      || entry.incidentData.incidentType
    )
  )
);

const buildTimelineEntryPresentation = ({
  entry,
  child = null,
  getContrastText,
  resolveCategoryColor,
}) => {
  let typeForTimeline;
  if (entry.collection === 'incidents') {
    typeForTimeline = 'incident';
  } else if (entry.collection === 'dailyCare') {
    typeForTimeline = 'dailyHabit';
  } else if (entry.collection === 'dailyLogs') {
    typeForTimeline = getLogTypeByEntry(entry).category || entry.category || entry.type || entry.incidentCategoryId || 'dailyLog';
  } else if (entry.collection === 'therapyNotes') {
    typeForTimeline = 'therapyNote';
  } else {
    typeForTimeline = entry.category || entry.type || entry.incidentCategoryId || 'dailyLog';
  }

  const entryType = mapLegacyType(typeForTimeline);
  const meta = getEntryTypeMeta(typeForTimeline);
  const defaultEntryLabel = meta.label.replace(/s$/, '');
  const resolvedCategory = entry.category || entry.type || entry.incidentCategoryId || 'log';
  const categoryMeta = getLogTypeByCategory(resolvedCategory);
  const careLogMeta = getCareLogCategoryMeta(resolvedCategory);
  const behaviorType = entry.behaviorType || getTimelineEntryBehaviorType(entry);
  const editTarget = entry.editTarget || getTimelineEntryEditTarget(entry);
  const isSmartCaptureMoodNote = behaviorType === 'narrative_note'
    && resolvedCategory === 'mood'
    && isSmartCaptureObservationEntry(entry);
  const isSmartCaptureSleepNote = behaviorType === 'narrative_note'
    && resolvedCategory === 'sleep'
    && (isSmartCaptureObservationEntry(entry) || entry.timestampSource === 'quick-note' || entry.timestampSource === 'local-input');
  const noteStyleLabel = isSmartCaptureMoodNote
    ? 'Mood note'
    : (isSmartCaptureSleepNote ? 'Sleep note' : '');
  const isBehaviorIncident = entry.collection === 'dailyLogs'
    && isBehaviorIncidentEntry(entry);
  const behaviorHeadline = isBehaviorIncident ? getBehaviorHeadline(entry) : '';
  const adaptiveResponsePresentation = isBehaviorIncident
    ? resolveAdaptiveResponsePresentation({ entry, child })
    : null;
  const adaptiveBehaviorLabel = adaptiveResponsePresentation?.categoryLabel || '';
  const meaningfulAdaptiveBehaviorLabel = adaptiveBehaviorLabel && adaptiveBehaviorLabel !== 'Behavior'
    ? adaptiveBehaviorLabel
    : '';
  const adaptiveFoodPresentation = entry.category === 'food'
    ? resolveAdaptiveFoodLogPresentation({ entry, child })
    : null;
  const adaptiveBathroomPresentation = (entry.category === 'bathroom' || entry.category === 'toileting')
    ? resolveAdaptiveBathroomLogPresentation({ entry, child })
    : null;
  const adaptiveFoodLabel = adaptiveFoodPresentation?.categoryLabel || '';
  const adaptiveBathroomLabel = adaptiveBathroomPresentation?.categoryLabel || '';
  const isMoodEntry = entry.type === 'mood'
    || entry.timelineType === 'mood'
    || entry.category === 'mood'
    || entry.actionType === 'mood'
    || entry.logCategory === 'mood';
  const moodValue = getMoodValue(entry);
  const entryLabel = entry.collection === 'incidents'
    ? (entry.incidentCategoryLabel || defaultEntryLabel)
    : entry.collection === 'dailyCare'
      ? (isMoodEntry
        ? LOG_TYPES.mood.displayLabel
        : (entry.categoryLabel || careLogMeta.label || categoryMeta?.trackLabel || defaultEntryLabel))
      : entry.collection === 'dailyLogs'
        ? (isBehaviorIncident
          ? (
            meaningfulAdaptiveBehaviorLabel
            || (!hasStructuredBehaviorIncidentData(entry) && entry.title)
            || behaviorHeadline
          )
          : (isMoodEntry
        ? (isSmartCaptureMoodNote ? 'Mood note' : LOG_TYPES.mood.displayLabel)
            : adaptiveFoodLabel
              || adaptiveBathroomLabel
              || (entry.titlePrefix
              || entry.title
              || entry.label
              || entry.categoryLabel
              || careLogMeta.label
              || categoryMeta?.trackLabel
              || categoryMeta?.displayLabel
              || defaultEntryLabel)))
        : (entry.title || defaultEntryLabel);
  const timelineColors = resolveCategoryColor(entry);
  const entryColor = timelineColors.dot;
  const entryIcon = isBehaviorIncident
    ? (careLogMeta.iconComponent || LOG_TYPES.behavior.iconComponent ? null : (careLogMeta.icon || LOG_TYPES.behavior.icon))
    : entry.collection === 'incidents'
      ? (entry.incidentCategoryIcon || meta.icon)
      : entry.collection === 'dailyCare'
        ? (isMoodEntry
          ? (entry.moodIconComponent || entry.categoryIconComponent || careLogMeta.iconComponent || LOG_TYPES.mood.iconComponent
            ? (entry.moodEmoji || entry.categoryIcon || null)
            : (entry.moodEmoji || entry.categoryIcon || careLogMeta.icon || LOG_TYPES.mood.icon))
          : (entry.categoryIconComponent || careLogMeta.iconComponent || meta.iconComponent
            ? (entry.categoryIcon || null)
            : (entry.categoryIcon || careLogMeta.icon || meta.icon)))
        : (entry.categoryIconComponent || careLogMeta.iconComponent || meta.iconComponent
          ? (entry.categoryIcon || null)
          : (entry.categoryIcon || careLogMeta.icon || meta.icon));
  const entryIconComponent = isBehaviorIncident
    ? (careLogMeta.iconComponent || LOG_TYPES.behavior.iconComponent || null)
    : entry.collection === 'incidents'
      ? (entry.incidentCategoryIconComponent || meta.iconComponent || careLogMeta.iconComponent || null)
      : entry.collection === 'dailyCare'
        ? (isMoodEntry
          ? (entry.moodIconComponent || entry.categoryIconComponent || careLogMeta.iconComponent || LOG_TYPES.mood.iconComponent || null)
          : (entry.categoryIconComponent || careLogMeta.iconComponent || meta.iconComponent || null))
        : (entry.categoryIconComponent || careLogMeta.iconComponent || meta.iconComponent || null);

  const labelTextColor = getContrastText(entryColor);
  const momentCount = Number(
    entry.momentCount
    || entry.storyMomentCount
    || entry.sourceStoryMoments?.length
    || entry.sourceStoryScenes?.length
    || entry.storyItem?.sourceStoryScenes?.length
    || entry.storyItem?.momentCount
    || 1
  );
  const resolvedMomentCount = Number.isFinite(momentCount) && momentCount > 0 ? momentCount : 1;
  const sourceMeta = entry.sourceMeta || entry.source || null;
  const sourceType = String(
    sourceMeta?.type
    || sourceMeta?.sourceType
    || entry.source?.type
    || entry.source?.sourceType
    || ''
  ).trim().toLowerCase();
  const isParsedSource = Boolean(
    sourceType !== 'manual'
    && (
      entry.parsedObservation
      || entry.source?.parsedObservation
      || entry.sourceText
      || entry.originalText
      || entry.metadata?.sourceSpanText
    )
  );
  const sourcePresentation = sourceMeta ? {
    type: sourceType || null,
    label: sourceMeta.senderName || sourceMeta.sourceLabel || sourceMeta.label || sourceMeta.title || '',
    isEmail: Boolean(sourceMeta.isEmail || sourceType === 'email'),
    isParsed: isParsedSource,
    needsReview: Boolean(sourceMeta.needsVerification),
    isManual: sourceType === 'manual',
  } : null;
  const isStory = Boolean(
    entry.isStory
    || entry.storyItem
    || entry.sourceStoryScenes?.length
    || entry.storyDisplayLabel
    || entry.storyLabel
    || entry.sourceStoryLabel
  );
  const status = {
    isEdited: Boolean(entry.editedAt || entry.editState === 'edited'),
    isPrivate: Boolean(entry.visibility === 'private' || entry.private === true || entry.isPrivate),
    needsReview: Boolean(sourcePresentation?.needsReview),
  };

  return {
    entryColor,
    entryIcon,
    entryIconComponent,
    entryLabel: noteStyleLabel || entryLabel,
    categoryLabel: noteStyleLabel || meaningfulAdaptiveBehaviorLabel || adaptiveFoodLabel || adaptiveBathroomLabel || entryLabel,
    entryType,
    behaviorType,
    editTarget,
    labelTextColor,
    meta,
    careLogMeta,
    moodValue,
    timelineColors,
    isStory,
    momentCount: resolvedMomentCount,
    hasMultipleMoments: resolvedMomentCount > 1,
    sourcePresentation,
    status,
  };
};

export { buildTimelineEntryPresentation };
